import { DEFAULT_MARKUP_PERCENT } from '@avrahami/contracts';

/**
 * Money: the API speaks minor units (agorot, integers); people type and read shekels.
 */

const SHEKELS_PATTERN = /^(\d{1,8})(?:[.,](\d{1,2}))?$/;

/**
 * Shekels as typed ("12", "12.5", "12,50", "1,234.50" is not accepted) to agorot. Empty text is
 * `null` (no amount); anything else that is not an amount is `NaN`, which the contract schemas
 * reject at the field's own path.
 */
export function shekelsToMinor(text: string): number | null {
  const value = text.trim();
  if (value === '') return null;
  const match = SHEKELS_PATTERN.exec(value);
  if (match === null) return Number.NaN;
  const whole = Number(match[1]);
  const fraction = Number((match[2] ?? '').padEnd(2, '0'));
  return whole * 100 + fraction;
}

/** Agorot to the text of a shekel input ("12", "12.50"); null is empty. */
export function minorToShekels(minor: number | null): string {
  if (minor === null) return '';
  // Amounts the store types are never negative; a negative one still reads correctly.
  const sign = minor < 0 ? '-' : '';
  const absolute = Math.abs(Math.trunc(minor));
  const whole = Math.floor(absolute / 100);
  const fraction = absolute % 100;
  return fraction === 0
    ? `${sign}${String(whole)}`
    : `${sign}${String(whole)}.${String(fraction).padStart(2, '0')}`;
}

/**
 * What the store earns on a product: its sale price less its cost, and that as a share of the
 * cost (100 = the sale price is twice the cost). Null while either amount is missing or
 * mistyped; `percent` is null over a cost of nothing.
 */
export function profitOf(
  costMinor: number | null,
  priceMinor: number | null,
): { amountMinor: number; percent: number | null } | null {
  if (costMinor === null || priceMinor === null) return null;
  if (!Number.isFinite(costMinor) || !Number.isFinite(priceMinor)) return null;
  const amountMinor = priceMinor - costMinor;
  return { amountMinor, percent: costMinor === 0 ? null : (amountMinor / costMinor) * 100 };
}

/** The sale price that leaves the default profit over a cost. */
export function defaultSalePrice(costMinor: number): number {
  return Math.round((costMinor * (100 + DEFAULT_MARKUP_PERCENT)) / 100);
}

const currency = new Intl.NumberFormat('he-IL', {
  style: 'currency',
  currency: 'ILS',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** A price to read: "‏12.50 ₪" in the Hebrew locale. */
export function formatMoney(minor: number): string {
  return currency.format(minor / 100);
}

/** A profit to read: the amount and, when there is one, its share of the cost ("100%"). */
export function formatProfit(profit: { amountMinor: number; percent: number | null }): string {
  const amount = formatMoney(profit.amountMinor);
  return profit.percent === null ? amount : `${amount} (${String(Math.round(profit.percent))}%)`;
}
